// src/portal-app/nav/dateRange.js
//
// Wave 2 Task 1 — shared local-time date-range helpers for the Insights
// surfaces (Financial Performance, Operational Efficiency, Product
// Intelligence, Cost Management). Pure functions, no React, no side effects.
//
// Why this file exists: the web portal's PortalReportingPage.js builds ISO
// date strings with `d.toISOString().split('T')[0]`, which reports the WRONG
// calendar day for any user whose local time is behind UTC (e.g. 23:30 local
// on the 15th is already the 16th in UTC). Every helper here is local-time
// throughout — never call toISOString() on a value that represents a
// calendar date.
//
// We deliberately re-implement the conventions already used by the web
// portal's src/pages/portal/reporting/_shared.js (toLocalISO, Monday-start
// weeks) rather than importing from it, so the portal-app stays
// self-contained and does not reach into `pages/portal/*`.
//
// DST safety: dates are always built via `new Date(y, m, d)` and mutated via
// `setDate()` — never by adding/subtracting 86400000ms to a local Date,
// which silently skips or repeats an hour across a DST transition. The one
// place we do divide by 86400000 (`daysBetweenUTC`) operates on values built
// with `Date.UTC(...)`, which has no DST, so that division is always exact —
// it is not the "add a day in local time" antipattern this rule guards
// against, it's just an integer day-count.

const MS_PER_DAY = 86400000;

/**
 * Formats a Date as a local-calendar 'YYYY-MM-DD' string.
 *
 * Uses getFullYear/getMonth/getDate (local time) — never toISOString(),
 * which reports the UTC calendar day and is wrong for any user behind UTC.
 *
 * @param {Date} date
 * @returns {string} 'YYYY-MM-DD' for the local calendar day `date` falls on
 */
export function toLocalISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** @returns {Date} a new Date at local midnight of the same calendar day as `date` */
function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** @returns {Date} `date` (at local midnight) shifted by `days` via setDate — DST-safe */
function addDays(date, days) {
    const d = startOfDay(date);
    d.setDate(d.getDate() + days);
    return d;
}

/** @returns {Date} local midnight of the Monday on or before `date` */
function startOfWeekMonday(date) {
    const d = startOfDay(date);
    const day = d.getDay(); // 0=Sun .. 6=Sat
    const diffFromMonday = day === 0 ? 6 : day - 1;
    d.setDate(d.getDate() - diffFromMonday);
    return d;
}

/** @returns {Date} local midnight of 1st of `date`'s month */
function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** @returns {Date} local midnight of 1st of `date`'s quarter (Jan/Apr/Jul/Oct) */
function startOfQuarter(date) {
    const quarterMonth = Math.floor(date.getMonth() / 3) * 3;
    return new Date(date.getFullYear(), quarterMonth, 1);
}

/** @returns {Date} local midnight of 1 Jan of `date`'s year */
function startOfYear(date) {
    return new Date(date.getFullYear(), 0, 1);
}

/** @returns {number} the last day-of-month number for a given year/monthIndex (0-11) */
function lastDayOfMonth(year, monthIndex) {
    // Day 0 of the next month is the last day of this month — a Date
    // constructor call, not millisecond arithmetic, so it's DST-safe and
    // correctly handles Feb in leap vs. non-leap years.
    return new Date(year, monthIndex + 1, 0).getDate();
}

/**
 * Counts the whole number of calendar days between two local dates (b - a).
 *
 * Built on Date.UTC() of the local y/m/d components rather than subtracting
 * the Date objects' own getTime() values, so the result is exact even when
 * `a` and `b` fall on opposite sides of a DST transition (UTC has no DST).
 * This is a day *count*, not a date being constructed — it never feeds a
 * `new Date(x.getTime() + n)` call.
 *
 * @param {Date} a
 * @param {Date} b
 * @returns {number} whole days from `a` to `b` (positive if b is later)
 */
function daysBetweenUTC(a, b) {
    const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.round((utcB - utcA) / MS_PER_DAY);
}

/**
 * Ordered preset list for the Insights date-range bar.
 *
 * `labelKey`/`fallback` follow this repo's i18n convention — the consuming
 * UI calls `t(preset.labelKey, {}, preset.fallback)`. No `t()` call happens
 * in this file.
 *
 * @type {Array<{id: string, labelKey: string, fallback: string}>}
 */
const PRESETS_LIST = [
    { id: 'today', labelKey: 'date_preset_today', fallback: 'Today' },
    { id: 'week', labelKey: 'date_preset_week', fallback: 'This Week' },
    { id: 'month', labelKey: 'date_preset_month', fallback: 'This Month' },
    { id: 'quarter', labelKey: 'date_preset_quarter', fallback: 'This Quarter' },
    { id: 'year', labelKey: 'date_preset_year', fallback: 'This Year' },
];
export const PRESETS = PRESETS_LIST;

/**
 * Resolves a preset id to a concrete { start, end } local-date range.
 *
 * Conventions (matching the web portal's reporting pages):
 * - `today`   — start === end === the calendar day of `now`.
 * - `week`    — the Monday–Sunday week containing `now` (Monday-start, so a
 *               week can extend into the future relative to `now`).
 * - `month`   — 1st of `now`'s month through `now` (month-to-date).
 * - `quarter` — 1st of `now`'s quarter through `now` (quarter-to-date).
 * - `year`    — 1 Jan of `now`'s year through `now` (year-to-date).
 *
 * @param {'today'|'week'|'month'|'quarter'|'year'} preset
 * @param {Date} [now] injectable "current time" for deterministic tests
 * @returns {{start: Date, end: Date}} local midnight Date objects, start <= end
 */
export function rangeForPreset(preset, now = new Date()) {
    const today = startOfDay(now);
    switch (preset) {
        case 'today':
            return { start: today, end: today };
        case 'week':
            return { start: startOfWeekMonday(today), end: addDays(startOfWeekMonday(today), 6) };
        case 'month':
            return { start: startOfMonth(today), end: today };
        case 'quarter':
            return { start: startOfQuarter(today), end: today };
        case 'year':
            return { start: startOfYear(today), end: today };
        default:
            throw new Error(`rangeForPreset: unknown preset "${preset}"`);
    }
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** @returns {string} localized short month name for `date`, e.g. "Jul" */
function shortMonth(date, locale) {
    try {
        return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
    } catch (e) {
        // Invalid/unsupported locale — fall back to the English table rather
        // than throwing, since this is a display label, not user input.
        return MONTH_SHORT[date.getMonth()];
    }
}

/**
 * Builds a human-readable label for a date range, e.g. "1–30 Jul 2026".
 *
 * Collapses to the shortest unambiguous form:
 * - same day:            "30 Jul 2026"
 * - same month & year:   "1–30 Jul 2026"
 * - same year:            "28 Jun – 5 Jul 2026"
 * - different years:      "28 Dec 2025 – 5 Jan 2026"
 *
 * @param {Date} start
 * @param {Date} end
 * @param {string} [locale] BCP-47 locale tag for month-name translation
 * @returns {string}
 */
export function formatRangeLabel(start, end, locale = 'en') {
    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();
    const sameDay = sameMonth && start.getDate() === end.getDate();

    const full = (d) => `${d.getDate()} ${shortMonth(d, locale)} ${d.getFullYear()}`;
    const dayMonth = (d) => `${d.getDate()} ${shortMonth(d, locale)}`;

    if (sameDay) return full(start);
    if (sameMonth) return `${start.getDate()}–${full(end)}`;
    if (sameYear) return `${dayMonth(start)} – ${dayMonth(end)} ${end.getFullYear()}`;
    return `${full(start)} – ${full(end)}`;
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** @returns {string} localized short weekday name for `date`, e.g. "Thu" */
function shortDay(date, locale) {
    try {
        return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
    } catch (e) {
        return DAY_SHORT[date.getDay()];
    }
}

/**
 * Parses a backend 'YYYY-MM-DD' string as LOCAL NOON of that calendar day.
 *
 * A bare `new Date('YYYY-MM-DD')` is parsed as UTC midnight by the spec, so
 * it lands on the WRONG calendar day for any user behind UTC — the exact bug
 * this module exists to kill. Appending 'T12:00:00' (a local-time form per
 * the spec) anchors the Date to local noon, which stays on the intended day
 * in every timezone (±12h) and across DST transitions. This mirrors the web
 * portal's reporting/_shared.js convention.
 *
 * @param {string} dateStr 'YYYY-MM-DD'
 * @returns {Date|null} local-noon Date, or null when unparseable
 */
function parseLocalDay(dateStr) {
    if (!dateStr) return null;
    const d = new Date(String(dateStr) + 'T12:00:00');
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * X-axis label for a daily trend row, e.g. "Thu 30".
 * @param {string} dateStr 'YYYY-MM-DD' (the /financial `sales_date` key)
 * @param {string} [locale] BCP-47 tag for weekday-name translation
 * @returns {string} "Thu 30" — or the raw input when unparseable
 */
export function formatDateLabel(dateStr, locale = 'en') {
    const d = parseLocalDay(dateStr);
    if (!d) return dateStr == null ? '' : String(dateStr);
    return `${shortDay(d, locale)} ${d.getDate()}`;
}

/**
 * X-axis label for a weekly trend row, e.g. "W4 Jul".
 * @param {string} dateStr 'YYYY-MM-DD' (the /financial `week_start` key)
 * @param {string} [locale] BCP-47 tag for month-name translation
 * @returns {string} "W4 Jul" — or the raw input when unparseable
 */
export function formatWeekLabel(dateStr, locale = 'en') {
    const d = parseLocalDay(dateStr);
    if (!d) return dateStr == null ? '' : String(dateStr);
    return `W${Math.ceil(d.getDate() / 7)} ${shortMonth(d, locale)}`;
}

/**
 * X-axis label for a monthly trend row, e.g. "Jul 26".
 * @param {string} dateStr 'YYYY-MM-DD' (the /financial `month_start` key)
 * @param {string} [locale] BCP-47 tag for month-name translation
 * @returns {string} "Jul 26" — or the raw input when unparseable
 */
export function formatMonthLabel(dateStr, locale = 'en') {
    const d = parseLocalDay(dateStr);
    if (!d) return dateStr == null ? '' : String(dateStr);
    return `${shortMonth(d, locale)} ${String(d.getFullYear()).slice(-2)}`;
}

/**
 * One label helper for every /financial `revenue_trend_type`, so the four
 * Insights surfaces never hand-roll the hourly/daily/weekly/monthly switch
 * (each hand-rolled copy is one more chance to reintroduce the UTC
 * off-by-one-day bug).
 *
 * Row key per trend type (reporting_v2.py):
 * - 'hourly'  → { hour: 0-23 }        → "13:00" (the web page's convention)
 * - 'daily'   → { sales_date }        → "Thu 30"
 * - 'weekly'  → { week_start }        → "W4 Jul"
 * - 'monthly' → { month_start }       → "Jul 26"
 * Unknown/missing trend types fall back to `sales_date` (the backend's own
 * default branch); junk rows yield ''.
 *
 * @param {object} row one element of the endpoint's trend array
 * @param {'hourly'|'daily'|'weekly'|'monthly'} trendType
 * @param {string} [locale] BCP-47 tag for name translation
 * @returns {string}
 */
export function trendLabel(row, trendType, locale = 'en') {
    if (!row) return '';
    switch (trendType) {
        case 'hourly': {
            const h = Number(row.hour);
            return Number.isFinite(h) ? `${String(h).padStart(2, '0')}:00` : '';
        }
        case 'weekly':
            return formatWeekLabel(row.week_start, locale);
        case 'monthly':
            return formatMonthLabel(row.month_start, locale);
        case 'daily':
        default:
            return row.sales_date != null ? formatDateLabel(row.sales_date, locale) : '';
    }
}

/**
 * Returns the immediately-preceding, equal-length window for a range.
 *
 * Mirrors the backend's `_prev_period` (reporting_v2.py / marketing_insights.py)
 * so client-computed labels agree with server-computed comparison periods:
 *
 * - If `start` is the 1st of a month (covers `month`/`quarter`/`year`
 *   presets, which are all "1st-of-period to date"), the previous period is
 *   the whole previous calendar month, with its end day capped to
 *   `min(end.day, previous month's last day)` — so a 31-day range compared
 *   against a 28/29-day February is correctly shortened rather than
 *   overflowing into March. This is the backend's exact behavior, including
 *   its edge case: a single-day range that happens to fall on the 1st of
 *   the month also takes this branch (comparing to the 1st of the previous
 *   month, not literally "yesterday").
 * - Otherwise, the previous period is the same inclusive day count,
 *   ending the day immediately before `start` (no gap, no overlap). This
 *   also covers the backend's Monday-start-week special case, which is
 *   mathematically identical to this default branch.
 *
 * @param {{start: Date, end: Date}} range
 * @returns {{start: Date, end: Date}} the preceding window, same shape
 */
export function previousRange({ start, end }) {
    const s = startOfDay(start);
    const e = startOfDay(end);

    if (s.getDate() === 1) {
        let prevMonth = s.getMonth() - 1;
        let prevYear = s.getFullYear();
        if (prevMonth < 0) {
            prevMonth = 11;
            prevYear -= 1;
        }
        const prevStart = new Date(prevYear, prevMonth, 1);
        const prevEndDay = Math.min(e.getDate(), lastDayOfMonth(prevYear, prevMonth));
        const prevEnd = new Date(prevYear, prevMonth, prevEndDay);
        return { start: prevStart, end: prevEnd };
    }

    const deltaDays = daysBetweenUTC(s, e) + 1; // inclusive day count
    const prevEnd = addDays(s, -1);
    const prevStart = addDays(prevEnd, -(deltaDays - 1));
    return { start: prevStart, end: prevEnd };
}
